import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table } from '@amzn/awsui-components-react';

import {
    getActivityTypesList,
    selectIsLoading,
    selectIsLoaded,
    selectAllActivityTypes,
    selectError,
    updateActivityType,
    selectSelectedActivityType,
    setSelectedActivityType,
    addActivityType,
    selectCount,
    resetActivityTypesSlice,
} from '../../../common/store/slices/activityTypesSlice';
import {
    AdminBusinessDataFormInputType,
    AdminBusinessDataFormSchema,
} from '../../interfaces/adminBusinessDataFormSchema';
import { AdminBusinessDataSelectors } from '../../interfaces/adminBusinessDataSelectors';
import AdminBusinessData from '../AdminBusinessData';
import { formatStatus } from '../AdminBusinessData/AdminBusinessData.Status';
import { useNotifications } from '../../../common/context/grimsbyNotifications';
import handleBusinessDataNotification from '../../../common/utils/handleBusinessDataNotification';
import parseBoolean from '../../../common/utils/parseBoolean';
import { ActivityTypeItemData } from '../../../common/interfaces/businessDataItem/activityTypeItem';

const ACTIVITY_TYPES_TABLE_TITLE = 'Activity types';
const ACTIVITY_TYPES_DISPLAY_SINGULAR = 'Activity type';
export const ACTIVITY_TYPE_NAME_KEY = 'activity_type';

// this array must exclude the "name" column because
// we need to define it inside AdminBusinessData so we can attach an eventHandler to that link
export const columnDefinitions: Array<Table.ColumnDefinition> = [
    {
        id: 'active',
        header: 'Status',
        cell: (activityType: ActivityTypeItemData) =>
            formatStatus(activityType.active.toString()),
    },
];

/**
 * Editable form fields for ActivityType
 * The keys must map to ActivityTypeItemData properties
 */
export const createFormSchema: AdminBusinessDataFormSchema<ActivityTypeItemData> =
    {
        // the keys must match ActivityTypeItem properties
        activity_type: {
            type: AdminBusinessDataFormInputType.Text,
            label: `${ACTIVITY_TYPES_DISPLAY_SINGULAR} name`,
            required: true,
            defaultValue: '',
            disabled: false,
            formDataTransform: (val: string): Partial<ActivityTypeItemData> => {
                return { activity_type: val };
            },
        },
        active: {
            type: AdminBusinessDataFormInputType.StatusRadio,
            label: 'Status',
            required: true,
            defaultValue: true,
            disabled: false,
            formDataTransform: (
                val: 'true' | 'false',
            ): Partial<ActivityTypeItemData> => {
                return { active: parseBoolean(val) };
            },
        },
    };

// The update form schema is the same as the create form schema except the "name" field is disabled
export const updateFormSchema: AdminBusinessDataFormSchema<ActivityTypeItemData> =
    {
        ...createFormSchema,
        activity_type: {
            ...createFormSchema.activity_type,
            disabled: true,
        },
    };

const AdminActivityTypes = () => {
    const dispatch = useDispatch();
    const { addNotification } = useNotifications();

    function useActivityTypes(): AdminBusinessDataSelectors<ActivityTypeItemData> {
        const dispatch = useDispatch();

        const items: ActivityTypeItemData[] = useSelector(
            selectAllActivityTypes,
        );
        const itemCount: number = useSelector(selectCount);
        const isLoading: boolean = useSelector(selectIsLoading);
        const isLoaded: boolean = useSelector(selectIsLoaded);
        const error = useSelector(selectError);
        const currentItem: ActivityTypeItemData | null = useSelector(
            selectSelectedActivityType,
        );
        useEffect(() => {
            dispatch(getActivityTypesList());
            dispatch(setSelectedActivityType(null));

            return () => {
                dispatch(resetActivityTypesSlice());
            };
        }, [dispatch]);
        return { items, itemCount, isLoading, isLoaded, error, currentItem };
    }

    const handleItemSelect = async (activityType: ActivityTypeItemData) => {
        if (activityType) {
            dispatch(setSelectedActivityType(activityType.activity_type));
            await dispatch(getActivityTypesList());
        }
    };

    const handleItemUpdate = async (data: ActivityTypeItemData) => {
        dispatch(setSelectedActivityType(null));
        const dispatchPromise = dispatch<any>(updateActivityType(data));
        await handleBusinessDataNotification({
            businessDataType: ACTIVITY_TYPE_NAME_KEY,
            businessDataDisplayNameLower:
                ACTIVITY_TYPES_DISPLAY_SINGULAR.toLowerCase(),
            itemName: data.activity_type,
            dispatchPromise: dispatchPromise,
            addNotification: addNotification,
            action: 'update',
        });
    };

    const handleItemCreate = async ({
        activity_type,
        active,
    }: ActivityTypeItemData) => {
        const dispatchPromise = dispatch<any>(
            addActivityType(active, activity_type),
        );
        await handleBusinessDataNotification({
            businessDataType: ACTIVITY_TYPE_NAME_KEY,
            businessDataDisplayNameLower:
                ACTIVITY_TYPES_DISPLAY_SINGULAR.toLowerCase(),
            itemName: activity_type,
            dispatchPromise: dispatchPromise,
            addNotification: addNotification,
            action: 'create',
        });
    };

    return (
        <div data-testid="AdminBusinessDataManagementActivityTypes">
            <AdminBusinessData
                columnDefinitions={columnDefinitions}
                createFormSchema={createFormSchema}
                updateFormSchema={updateFormSchema}
                handleItemCreate={handleItemCreate}
                handleItemSelect={handleItemSelect}
                handleItemUpdate={handleItemUpdate}
                itemDisplayNameSingular={ACTIVITY_TYPES_DISPLAY_SINGULAR}
                itemNameKey={ACTIVITY_TYPE_NAME_KEY}
                title={ACTIVITY_TYPES_TABLE_TITLE}
                useItemList={useActivityTypes}
            />
        </div>
    );
};

export default AdminActivityTypes;
